'use client';

import { X } from 'lucide-react';
import {
	SIDE_PANEL_BODY_PORTAL_ID,
	SIDE_PANEL_RAIL_PORTAL_ID,
	useSidePanelShell,
} from './SidePanelContext';
import styles from './AppShell.module.css';

/**
 * El panel derecho. Vive fuera de `AppShell` porque CONSUME el contexto que
 * `AppShell` provee, y un componente no puede leer el contexto que él mismo monta.
 *
 * En modo `rail` monta también la columna de iconos y ocupa ancho en reposo; en
 * `toggle` no hay carril: el panel no ocupa nada hasta que se abre.
 *
 * El shell aporta la caja, el ancho, la animación y el encabezado; NO aporta el
 * botón que abre el panel. Ese lo pone la vista —en el carril si lo hay, en su
 * propia barra si no—, porque es la única que sabe cómo se llama su panel y qué
 * contador mostrarle.
 *
 * Los dos `div` de portal se renderizan siempre, incluso sin panel declarado: si
 * no existieran en el DOM, la vista no tendría dónde colgar su contenido.
 */
export function SidePanelAside() {
	const { active, mode, open, animate, setOpen, title } = useSidePanelShell();
	const coversContent = mode === 'toggle';

	return (
		<>
			{active && open && coversContent ? (
				// El velo existe sólo para el modo `toggle`, y sólo se ve donde el panel
				// tapa contenido — en pantallas angostas. En escritorio el panel empuja,
				// así que no hay nada tapado que cerrar tocando fuera.
				<div
					className={styles.appShellSidePanelScrim}
					onClick={() => setOpen(false)}
					aria-hidden="true"
				/>
			) : null}
			<aside
				className={styles.appShellSidePanel}
				data-active={active}
				data-mode={mode}
				data-open={active && open}
				data-animate={animate}
				aria-hidden={!active}
			>
				<div className={styles.appShellSidePanelInner}>
					{/* El carril de iconos sólo tiene sentido con el panel cerrado y
					    presente, es decir en `rail`. En `toggle` el hueco se monta igual
					    —el portal necesita existir— pero no se muestra. */}
					<div id={SIDE_PANEL_RAIL_PORTAL_ID} className={styles.appShellSidePanelRail} />
					<div className={styles.appShellSidePanelBody}>
						<div className={styles.appShellSidePanelHeader}>
							<span className="truncate">{title}</span>
							<button
								type="button"
								onClick={() => setOpen(false)}
								aria-label="Cerrar panel"
								title="Cerrar panel"
								className={styles.appShellSidePanelClose}
							>
								<X size={15} />
							</button>
						</div>
						<div id={SIDE_PANEL_BODY_PORTAL_ID} className={styles.appShellSidePanelContent} />
					</div>
				</div>
			</aside>
		</>
	);
}
